import React from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, ChevronRight, Info } from 'lucide-react';
import { getAdAttribution, attributionLeadsQuery, shortId, CHANNEL_LABELS } from '../utils/adAttribution';

// "Which ad did this lead come from?" — one compact, wrap-friendly line:
//   [Meta Ads] Spring Promo › Homeowners 35+ › Video A · Lead form
// Every crumb links to the list the lead lives in (Leads, or Customers for
// a won lead), filtered to that campaign / ad set / ad, so an operator can
// jump from one lead to "everyone from this ad".
// Renders nothing when the lead carries no paid-channel IDs.

const PLATFORM_STYLES = {
  meta: 'bg-blue-50 text-blue-700 border-blue-200',
  google: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  tiktok: 'bg-gray-900 text-white border-gray-900',
};

const Crumb = ({ node, kind, query, last, basePath }) => {
  const label = node?.name || (node?.id ? `${kind} ${shortId(node.id)}` : null);
  if (!label) return null;
  return (
    <>
      <Link
        to={`${basePath}?${query}`}
        title={node?.id ? `${kind} ID ${node.id} — show all leads from this ${kind.toLowerCase()}` : undefined}
        className={`hover:underline truncate max-w-[14rem] sm:max-w-xs ${node?.name ? 'text-gray-800' : 'text-gray-500 font-mono text-[11px]'}`}
      >
        {label}
      </Link>
      {!last && <ChevronRight size={11} className="text-gray-300 shrink-0" />}
    </>
  );
};

// maxCrumbs: show only the first N of campaign › ad set › ad (list cells).
const AdAttributionBadge = ({ lead, attribution: attributionProp, compact = false, className = '', basePath: basePathProp, maxCrumbs }) => {
  const attr = attributionProp || getAdAttribution(lead);
  if (!attr) return null;
  const basePath = basePathProp || (lead?.status === 'won' ? '/customers' : '/leads');

  const crumbs = [
    { node: attr.campaign, kind: 'Campaign', query: attributionLeadsQuery({ platform: attr.platform, campaignId: attr.campaign?.id }) },
    { node: attr.adset, kind: 'Ad set', query: attributionLeadsQuery({ platform: attr.platform, adsetId: attr.adset?.id }) },
    { node: attr.ad, kind: 'Ad', query: attributionLeadsQuery({ platform: attr.platform, adId: attr.ad?.id }) },
  ].filter((c) => c.node?.name || c.node?.id).slice(0, maxCrumbs || 3);

  const hasNames = crumbs.some((c) => c.node?.name);
  const channelLabel = attr.channel && attr.channel !== 'lead_form' ? CHANNEL_LABELS[attr.channel] : null;

  return (
    <div className={`flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs ${className}`}>
      <Link
        to={`${basePath}?${attributionLeadsQuery({ platform: attr.platform })}`}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${PLATFORM_STYLES[attr.platform] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
        title="Show all leads from this ad platform"
      >
        <Megaphone size={11} />
        {attr.platformLabel}
      </Link>

      {crumbs.length > 0 ? (
        <span className="inline-flex flex-wrap items-center gap-1 min-w-0">
          {crumbs.map((c, i) => (
            <Crumb key={c.kind} node={c.node} kind={c.kind} query={c.query} last={i === crumbs.length - 1} basePath={basePath} />
          ))}
        </span>
      ) : attr.isOrganic ? (
        <span className="text-gray-500">Organic — form shared without an ad</span>
      ) : null}

      {!compact && channelLabel && (
        <span className="text-gray-400 whitespace-nowrap">· {channelLabel}</span>
      )}

      {!compact && attr.platform === 'meta' && !hasNames && crumbs.length > 0 && (
        <span
          className="inline-flex items-center gap-1 text-gray-400"
          title="Ad names haven't been synced for this lead yet. Settings → Inbound Channels → Sync ad names."
        >
          <Info size={11} /> names not synced
        </span>
      )}
    </div>
  );
};

export default AdAttributionBadge;
